import { useState, useRef, useCallback, useEffect } from 'react';
import { ASRProvider, RecordingState } from '~/types';
import { convertToWAV, computeSHA256 } from '~/lib/wav-converter';
import { enqueueSegment } from '~/lib/indexed-db';
import { createSession, updateSession } from '~/lib/api';

const SEGMENT_DURATION_MS = 20000; // 20 seconds

export function useRecorder() {
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    duration: 0,
    sessionId: null,
    provider: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const segmentIndexRef = useRef(0);
  const startTimeRef = useRef(0);
  const segmentStartTimeRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const segmentTimerRef = useRef<number | null>(null);

  /**
   * Start recording
   */
  const startRecording = useCallback(async (provider: ASRProvider, title?: string) => {
    try {
      // Create session
      const { session_id } = await createSession(provider, title);

      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      segmentIndexRef.current = 0;
      startTimeRef.current = Date.now();
      segmentStartTimeRef.current = Date.now();

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Start recording
      mediaRecorder.start();

      // Start segment timer
      scheduleNextSegment();

      // Start duration timer
      timerRef.current = window.setInterval(() => {
        setState(prev => ({
          ...prev,
          duration: Date.now() - startTimeRef.current,
        }));
      }, 100);

      setState({
        isRecording: true,
        isPaused: false,
        duration: 0,
        sessionId: session_id,
        provider,
      });
    } catch (error) {
      console.error('Failed to start recording:', error);
      throw error;
    }
  }, []);

  /**
   * Pause recording
   */
  const pauseRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || !state.sessionId) return;

    // Finalize current segment
    await finalizeCurrentSegment();

    // Pause media recorder
    mediaRecorderRef.current.pause();

    // Clear segment timer
    if (segmentTimerRef.current !== null) {
      clearTimeout(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }

    // Update session status
    await updateSession(state.sessionId, 'paused');

    setState(prev => ({ ...prev, isPaused: true }));
  }, [state.sessionId]);

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || !state.sessionId) return;

    // Resume media recorder
    mediaRecorderRef.current.resume();

    // Reset segment start time
    segmentStartTimeRef.current = Date.now();

    // Schedule next segment
    scheduleNextSegment();

    // Update session status
    await updateSession(state.sessionId, 'recording');

    setState(prev => ({ ...prev, isPaused: false }));
  }, [state.sessionId]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) return;

    // Finalize current segment
    await finalizeCurrentSegment();

    // Stop media recorder
    mediaRecorderRef.current.stop();

    // Stop stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Clear timers
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (segmentTimerRef.current !== null) {
      clearTimeout(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }

    const finalSessionId = state.sessionId;

    setState({
      isRecording: false,
      isPaused: false,
      duration: 0,
      sessionId: null,
      provider: null,
    });

    return finalSessionId;
  }, [state.sessionId]);

  /**
   * Schedule next segment
   */
  const scheduleNextSegment = () => {
    segmentTimerRef.current = window.setTimeout(async () => {
      await finalizeCurrentSegment();
      scheduleNextSegment();
    }, SEGMENT_DURATION_MS);
  };

  /**
   * Finalize current segment
   */
  const finalizeCurrentSegment = async () => {
    if (!mediaRecorderRef.current || !state.sessionId || chunksRef.current.length === 0) {
      return;
    }

    // Request data from recorder
    mediaRecorderRef.current.requestData();

    // Wait a bit for data to be available
    await new Promise(resolve => setTimeout(resolve, 100));

    // Create blob from chunks
    const webmBlob = new Blob(chunksRef.current, { type: 'audio/webm' });

    // Convert to WAV
    const wavBlob = await convertToWAV(webmBlob);

    // Compute SHA256
    const sha256 = await computeSHA256(wavBlob);

    // Calculate timing
    const now = Date.now();
    const startMs = segmentStartTimeRef.current - startTimeRef.current;
    const endMs = now - startTimeRef.current;

    // Enqueue for upload
    await enqueueSegment({
      sessionId: state.sessionId,
      segmentIndex: segmentIndexRef.current,
      startMs,
      endMs,
      sha256,
      wavBlob,
      state: 'queued',
      retryCount: 0,
      nextRetryAt: Date.now(),
    });

    // Reset for next segment
    chunksRef.current = [];
    segmentIndexRef.current++;
    segmentStartTimeRef.current = now;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && state.isRecording) {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
      if (segmentTimerRef.current !== null) {
        clearTimeout(segmentTimerRef.current);
      }
    };
  }, []);

  return {
    state,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
  };
}
